/* eslint-disable import/no-cycle */
import {
  IApplicationSettings,
  IBadge, IBoundCorporate,
  IData,
  IResponse, IUserData, IUserInfo, IUserNotificationsSettings, IUserReShareSettings, IUserSettings,
} from '@/types/interfaces';
import ApiService from '@/services/ApiService';
import {
  ACCOUNT_STATUS, BADGES_TYPES, REGISTRATION_STATE, USER_STATUS,
} from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import WebsocketService from './WebsocketService';
import LocalStorageService from '@/services/LocalStorageService';
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import NativeService from './NativeService';
import ToFlutterService from './ToFlutterService';

// import { ACCOUNT_STATUS, REGISTRATION_STATE, USER_STATUS } from '@/types/enums';

export default class UserService {
  public static fetchUser(): Promise<IResponse<IUserData>> {
    return ApiService.getAndReturnData('user');
  }

  public static fetchUserBadges(): Promise<IResponse<IData<IBadge[]>>> {
    return ApiService.getAndReturnData('user/notifications/badges');
  }

  public static fetchUserSettings(): Promise<IResponse<IData<IUserSettings>>> {
    return ApiService.getAndReturnData('user/settings');
  }

  public static fetchBoundCorporate(): Promise<IResponse<IData<IBoundCorporate>>> {
    return ApiService.getAndReturnData('user/get-bound-corporate');
  }

  public static fetchApplicationSettings(): Promise<IResponse<IData<IApplicationSettings[]>>> {
    return ApiService.getAndReturnData('features');
  }

  public static getGoogleTagManagerId(): Promise<IResponse<IData<{GTM_ID: string}>>> {
    return ApiService.getAndReturnData('statistic/gtm-id');
  }

  private static prefetchAppData() {
    const alertsStore = useAlertsStore();
    const spacesStore = useSpacesStore();
    const driveStore = useDriveStore();
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();

    alertsStore.fetchNotifications();

    spacesStore.fetchInvitationsContact();
    spacesStore.fetchInvitationsGroup();
    spacesStore.fetchRequestsToMyGroups();
    spacesStore.fetchSuggestions();
    spacesStore.fetchMyContacts();
    spacesStore.fetchOutgoingContact();
    spacesStore.fetchOutgoingGroup();
    spacesStore.fetchMyGroups();
    spacesStore.fetchUsersGroups();

    driveStore.fetchCases();
    driveStore.fetchNetworkCases(true);
    driveStore.fetchNetworkPhotos();
    driveStore.fetchPhotos();
    driveStore.fetchNetworkMixedData();
    driveStore.fetchMixedData();
    driveStore.fetchPublicCases();

    messagesStore.fetchConversations();
    messagesStore.fetchGroupsForChat();

    mainStore.fetchInputLabels();
    mainStore.fetchPersonalLabels();
    mainStore.fetchSubscriptionQuota();
    mainStore.fetchNotificationSettings();
    mainStore.fetchSharingSettings();
    mainStore.fetchUserBadges();
  }

  public static fetchAndStoreUser(): Promise<unknown> {
    return Promise.all(
      [
        this.fetchUser(), this.fetchUserSettings(),
        this.fetchApplicationSettings(), this.getGoogleTagManagerId(), WebsocketService.connectWS(),
      ],
    )
      .then(([userRes, userSettings, applicationSettings, googleTagManagerId, websocket]) => {
        let user = {} as IUserInfo;
        if (userRes.success) {
          user = (userRes.results as IUserData).user;
        } else if (userRes.errorKey === 'REGISTRATION_INCOMPLETE') {
          user = (userRes.results as IUserData).user;
        }
        user.status = this.getUserStatus(user.registrationState, user.accountStatuses);
        const mainStore = useMainStore();
        mainStore.$state.user = user;

        mainStore.userSettings = userSettings.results.data;

        if (user.accountStatuses.includes(ACCOUNT_STATUS.SUB_ACCOUNT)) {
          this.fetchBoundCorporate().then((corporateRes) => {
            mainStore.boundCorporate = corporateRes.results.data;
          });
        }

        mainStore.applicationSettings = applicationSettings.results.data;

        LocalStorageService.setGmtId(googleTagManagerId.results.data.GTM_ID);

        NativeService.connectToNative();
        ToFlutterService.connect();
        this.prefetchAppData();

        return userRes;
      });
  }

  public static getUserStatus(registrationState: REGISTRATION_STATE, accountStatus: ACCOUNT_STATUS[]): USER_STATUS {
    if (registrationState === REGISTRATION_STATE.NOT_REGISTERED) {
      return USER_STATUS.NOT_REGISTERED;
    }
    if (registrationState === REGISTRATION_STATE.CONFIRM_PHONE_NUMBER) {
      return USER_STATUS.CONFIRM_PHONE_NUMBER;
    }
    if (registrationState === REGISTRATION_STATE.CONFIRM_EMAIL) {
      return USER_STATUS.CONFIRM_EMAIL;
    }
    if (registrationState === REGISTRATION_STATE.COMPLETED) {
      return USER_STATUS.COMPLETED;
    }
    if (accountStatus.includes(ACCOUNT_STATUS.REGISTERED)) {
      return USER_STATUS.LOGGED_IN;
    }
    return USER_STATUS.UNKNOWN;
  }

  public static getPApiToken(): Promise<IResponse<any>> {
    return ApiService.getAndReturnData('user/retrieve-api-key');
  }

  public static regeneratePApiToken(): Promise<IResponse<any>> {
    return ApiService.postAndReturnData('user/regenerate-api-key');
  }

  public static getNotificationSettings(): Promise<IResponse<IData<IUserNotificationsSettings>>> {
    return ApiService.getAndReturnData('user/notifications/settings');
  }

  public static setNotificationSettings(payload: IUserNotificationsSettings): Promise<IResponse<IData<IUserNotificationsSettings>>> {
    return ApiService.postAndReturnData('user/notifications/settings/save', payload);
  }

  public static getReSharingSettings(): Promise<IResponse<IData<IUserReShareSettings[]>>> {
    return ApiService.getAndReturnData('shares/settings');
  }

  public static setReSharingSettings(payload: IUserReShareSettings[]): Promise<IResponse<IData<IUserReShareSettings[]>>> {
    return ApiService.postAndReturnData('shares/settings/save', payload);
  }

  public static getQuota(): Promise<IResponse<any>> {
    return ApiService.getAndReturnData('subscription/quota');
  }

  public static readNotification(payload): Promise<IResponse<IData<any>>> {
    return ApiService.postAndReturnData('user/notifications/read', payload);
  }

  public static getUserTutorialInfo(): Promise<IResponse<any>> {
    return ApiService.postAndReturnData('user/tutorial/complete');
  }
}
